import Button from "components/button"
import { Checkbox } from "components/check-box/Checkbox"
import Input from "components/input"
import type global from "global"
import type React from "react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import request from "request"
import { useDispatch, useSelector } from "store/hooks"
import type users from "../users"
import styles from "./create-modal.module.scss"

interface UserPermission {
  title: string
  value: string
  checked: boolean
}

interface ValidationError {
  name: "email" | "password1" | "password2" | "role"
  value: string
}

interface CreateUserModalProps {
  onClose: () => void
  onCreate: () => void
  roles: users.roles[]
}

const CreateUserModal = ({
  onClose,
  onCreate,
  roles,
}: CreateUserModalProps) => {
  const user = useSelector((state) => state.user)
  const dispatch = useDispatch()
  const rolePermissionState = useSelector((state) => state.permissions.data)

  const [email, setEmail] = useState("")
  const [password1, setPassword1] = useState("")
  const [password2, setPassword2] = useState("")
  const [role, setRole] = useState<global.Role | null>(null)

  const [pending, setPending] = useState(false)
  const [validError, setValidError] = useState<ValidationError | null>(null)
  const [userPermission, setUserPermission] = useState<UserPermission[]>([])

  const validateForm = useCallback((): ValidationError | null => {
    const availableRoles = getAvailableRoles()
    if (availableRoles.length === 0) {
      return {
        name: "role",
        value: "У вас нет прав для создания пользователей",
      }
    }

    if (!email.trim()) {
      return {
        name: "email",
        value: "Email is required",
      }
    }

    if (email.trim().length < 5) {
      return {
        name: "email",
        value: "Minimum email length 5 characters",
      }
    }

    if (!password1.trim()) {
      return {
        name: "password1",
        value: "Password is required",
      }
    }

    if (password1.trim().length < 6) {
      return {
        name: "password1",
        value: "Minimum password length 6 characters",
      }
    }

    if (password1.trim() !== password2.trim()) {
      return {
        name: "password2",
        value: "The passwords don't match",
      }
    }

    if (!role) {
      return {
        name: "role",
        value: "Role is required",
      }
    }

    return null
  }, [email, password1, password2, role])

  const handleCreate = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const validationError = validateForm()
      if (validationError) {
        setValidError(validationError)
        return
      }

      setValidError(null)
      setPending(true)

      try {
        const permissions: string[] =
          role === "custom"
            ? userPermission
                .filter((item) => item.checked && item.value)
                .map((item) => item.value)
            : []

        await request.post("/admin/users", {
          email: email.toLowerCase().trim(),
          password1: password1.trim(),
          password2: password2.trim(),
          role: role,
          permissions: permissions,
        })

        toast.success("User created successfully")
        onCreate()
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || "Error creating user"
        toast.error(errorMessage)
      } finally {
        setPending(false)
      }
    },
    [validateForm, email, password1, password2, role, userPermission, onCreate],
  )

  const handlePermissionChange = useCallback(
    (idx: number) => {
      if (role !== "custom") return

      setUserPermission((prevPermissions) =>
        prevPermissions.map((permission, index) =>
          index === idx
            ? { ...permission, checked: !permission.checked }
            : permission,
        ),
      )
    },
    [role],
  )
  useEffect(() => {
    if (!role) return

    setUserPermission([])

    if (role === "custom") {
      dispatch.permissions.getAllPermissions()
    } else {
      dispatch.permissions.rolePermission({ role: role as string })
    }
  }, [role, dispatch])

  useEffect(() => {
    if (!role || !rolePermissionState) return

    if (role === "custom") {
      const allPermissions = Array.isArray(rolePermissionState)
        ? rolePermissionState.map((permission: any) => {
            return {
              title: permission.name || permission.title || permission,
              value: permission.key || permission.value || permission,
              checked: false,
            }
          })
        : []
      setUserPermission(allPermissions)
    } else {
      const backendPermissions: string[] = Array.isArray(rolePermissionState)
        ? rolePermissionState
        : []

      const allPermissions = Array.isArray(rolePermissionState)
        ? rolePermissionState.map((permission: any) => ({
            title: permission.name || permission,
            value: permission.key || permission,
            checked: backendPermissions.includes(permission.key || permission),
          }))
        : []
      setUserPermission(allPermissions)
    }
  }, [role, rolePermissionState])

  const getAvailableRoles = useCallback((): [string, string][] => {
    const currentRole = user?.role

    if (currentRole === "root") {
      return roles.map((role) => [String(role), String(role)])
    }

    if (currentRole === "admin") {
      return roles
        .filter((role) => role === "expert" || role === "custom")
        .map((role) => [String(role), String(role)])
    }

    if (currentRole === "custom") {
      const hasCreateUserPermission = user?.permissions?.includes("create_user")
      if (hasCreateUserPermission) {
        return roles
          .filter((role) => role === "expert")
          .map((role) => [String(role), String(role)])
      }
      return []
    }

    return []
  }, [user?.role, user?.permissions, roles])

  const canCreateCustomRole = user?.role === "root" || user?.role === "admin"

  return (
    <div className={styles.modal} onMouseDown={onClose}>
      <form
        className={styles.modalWrap}
        onSubmit={handleCreate}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.title}>New user</div>
        <div className={styles.inputs}>
          <div className={styles.input}>
            <Input
              title="Email"
              type="string"
              value={email}
              error={
                validError?.name === "email" ? validError.value : undefined
              }
              onInput={(value) => setEmail(value)}
            />
          </div>
          <div className={styles.input}>
            <Input
              title="Password"
              type="password"
              value={password1}
              error={
                validError?.name === "password1" ? validError.value : undefined
              }
              onInput={(value) => setPassword1(value)}
            />
          </div>
          <div className={styles.input}>
            <Input
              title="Confirm password"
              type="password"
              value={password2}
              error={
                validError?.name === "password2" ? validError.value : undefined
              }
              onInput={(value) => setPassword2(value)}
            />
          </div>
          <div className={styles.input}>
            <Input
              id="role"
              title="Role"
              type="select"
              value={String(role) ?? ""}
              error={validError?.name === "role" ? validError.value : undefined}
              onSelect={(option) => setRole(option.value as global.Role)}
              options={getAvailableRoles()
                .filter(([_, value]) => {
                  if (value === "custom") {
                    return canCreateCustomRole
                  }
                  return true
                })
                .map(([value, key]) => ({
                  value: value,
                  key: key,
                }))}
            />
          </div>
        </div>
        {role && (
          <>
            <div className={styles.title}>
              {role === "custom" ? "Access settings" : "Role access"}
            </div>
            <div className={styles.inputs}>
              <div className={styles.input}>
                {userPermission?.map((item, idx) => (
                  <Checkbox
                    checked={item?.checked}
                    title={item?.title}
                    disabled={role !== "custom"}
                    onChange={() => handlePermissionChange(idx)}
                    key={item?.title}
                  />
                ))}
              </div>
            </div>
          </>
        )}
        <div className={styles.buttonGroup}>
          <Button color="orange" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="green" pending={pending}>
            Create
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CreateUserModal
